import { Avatar, Button, Chip, Skeleton } from "@heroui/react";
import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import { memo, useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import { isInteractive } from "@funnel-ui/primitives/isInteractive";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

type MatchRevealProps = {
  /** Small uppercase label rendered above the headline in accent color. */
  eyebrow?: string;
  headline?: string;
  description?: string;
  characterName: string;
  characterTag?: string;
  characterImageUrl?: string;
  badge?: string;
  message: string;
  avatarUrl?: string;
  ctaLabel?: string;
  onCta?: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  restartLabel?: string;
  onRestart?: () => void;
  typingMs?: number;
  /** Called when the user taps/clicks anywhere on the step (outside interactive elements). */
  onPress?: () => void;
  className?: string;
  testId?: string;
};

const TYPING_MS_CAP = 1200;
const CHAR_DELAY_MS = 30;

const PortraitCard = ({
  imageUrl,
  name,
  tag,
  badge,
}: {
  imageUrl?: string;
  name: string;
  tag?: string;
  badge?: string;
}) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative w-full overflow-hidden rounded-3xl" style={{ minHeight: 320 }}>
      {!loaded && <Skeleton aria-hidden="true" className="absolute inset-0 h-full w-full" />}
      <ResponsiveAsset
        src={imageUrl}
        alt={name}
        loading="eager"
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className="h-full w-full object-cover transition-opacity duration-300"
        style={{ opacity: loaded ? 1 : 0.001 }}
      />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      <div className="absolute inset-x-0 bottom-0 z-20 flex flex-col gap-1 p-5">
        {badge && (
          <span className="mb-1 self-start">
            <Chip color="accent" size="sm" variant="soft">
              {badge}
            </Chip>
          </span>
        )}
        <span className="text-xl font-semibold text-white">{name}</span>
        {tag && <span className="text-sm text-white/70">{tag}</span>}
      </div>
    </div>
  );
};

const TypingDots = () => (
  <span aria-hidden="true" className="inline-flex items-center gap-1 px-1 py-1">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="block h-1.5 w-1.5 rounded-full"
        style={{
          background: "hsl(var(--brand-bg-foreground) / 0.55)",
          animation: `fc-typing-bounce 900ms ${i * 120}ms infinite ease-in-out`,
        }}
      />
    ))}
  </span>
);

const TypewriterBubble = ({
  message,
  typingMs,
  avatarUrl,
  prefersReduced,
  testId,
}: {
  message: string;
  typingMs: number;
  avatarUrl?: string;
  prefersReduced: boolean;
  testId?: string;
}) => {
  const [phase, setPhase] = useState<"typing" | "revealing" | "done">(
    prefersReduced ? "done" : "typing",
  );
  const [charIndex, setCharIndex] = useState(0);
  const rafRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (prefersReduced) {
      setPhase("done");
      return;
    }
    const handle = setTimeout(() => setPhase("revealing"), Math.min(typingMs, TYPING_MS_CAP));
    return () => clearTimeout(handle);
  }, [typingMs, prefersReduced]);

  useEffect(() => {
    if (phase !== "revealing") return;
    if (charIndex >= message.length) {
      setPhase("done");
      return;
    }
    rafRef.current = setTimeout(() => setCharIndex((c) => c + 1), CHAR_DELAY_MS);
    return () => clearTimeout(rafRef.current);
  }, [phase, charIndex, message.length]);

  const displayText = phase === "done" ? message : message.slice(0, charIndex);

  return (
    <div className="flex items-start gap-3">
      {avatarUrl && (
        <Avatar size="sm" className="shrink-0">
          <Avatar.Image src={avatarUrl} alt="" />
          <Avatar.Fallback>?</Avatar.Fallback>
        </Avatar>
      )}
      <div
        data-testid={testId ? `${testId}-bubble` : undefined}
        className="max-w-[85%] rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed"
        style={{
          background: "hsl(var(--brand-bg-foreground) / 0.06)",
          color: "hsl(var(--brand-bg-foreground))",
        }}
      >
        <AnimatePresence initial={false} mode="popLayout">
          {phase === "typing" ? (
            <m.span
              key="typing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="inline-flex"
            >
              <TypingDots />
            </m.span>
          ) : (
            <m.span
              key="text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              {displayText}
              {phase === "revealing" && (
                <span
                  className="inline-block h-4 w-0.5 animate-pulse align-middle"
                  style={{ background: "hsl(var(--brand-primary))" }}
                />
              )}
            </m.span>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const MatchRevealImpl = ({
  eyebrow,
  headline,
  description,
  characterName,
  characterTag,
  characterImageUrl,
  badge,
  message,
  avatarUrl,
  ctaLabel = "Start chat",
  onCta,
  secondaryLabel,
  onSecondary,
  restartLabel,
  onRestart,
  onPress,
  typingMs = 700,
  className,
  testId,
}: MatchRevealProps) => {
  const prefersReduced = useReducedMotion();
  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!onPress || isInteractive(e.target)) return;
      onPress();
    },
    [onPress],
  );

  return (
    <section
      data-testid={testId}
      className={`flex flex-col items-center gap-6 ${className ?? ""}${onPress ? " cursor-pointer" : ""}`}
      aria-label={`Match reveal: ${characterName}`}
      onClick={handleClick}
    >
      {(eyebrow || headline) && (
        <StepHeading
          title={headline ?? ""}
          description={description}
          eyebrow={eyebrow}
          align="center"
        />
      )}

      <PortraitCard
        imageUrl={characterImageUrl}
        name={characterName}
        tag={characterTag}
        badge={badge}
      />

      <TypewriterBubble
        message={message}
        typingMs={typingMs}
        avatarUrl={avatarUrl}
        prefersReduced={!!prefersReduced}
        testId={testId}
      />

      <div className="flex w-full flex-col items-center gap-3">
        <Button variant="primary" size="lg" fullWidth onPress={onCta} className="rounded-full">
          {ctaLabel}
        </Button>
        {onSecondary && secondaryLabel && (
          <Button variant="ghost" size="sm" onPress={onSecondary}>
            {secondaryLabel}
          </Button>
        )}
        {onRestart && restartLabel && (
          <Button variant="ghost" size="sm" onPress={onRestart} className="opacity-50">
            {restartLabel}
          </Button>
        )}
      </div>
    </section>
  );
};

export const MatchReveal = memo(MatchRevealImpl);

export default MatchReveal;
